import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Pencil } from "lucide-react";
import api from "@/lib/api";
import {
  FALLBACK_PRODUCTS,
  productImageSrc,
  FALLBACK_IMG,
  productWaMessage,
  waLink,
  REVEAL,
} from "@/lib/site";

export const Showcase = () => {
  const [products, setProducts] = useState(FALLBACK_PRODUCTS);
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);

  const openProduct = (p) => {
    setActive(p);
    setImgIdx(0);
  };

  const galleryOf = (p) => [...new Set([p?.image, ...(p?.images || [])].filter(Boolean))];

  useEffect(() => {
    if (window.__lenis) {
      if (active) window.__lenis.stop();
      else window.__lenis.start();
    }
    document.body.style.overflow = active ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [active]);

  useEffect(() => {
    api
      .get("/products")
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length) setProducts(r.data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handler = (e) => setFilter(e.detail || "All");
    window.addEventListener("jk:filter", handler);
    return () => window.removeEventListener("jk:filter", handler);
  }, []);

  const tabs = useMemo(() => {
    const cats = [...new Set(products.map((p) => p.category).filter(Boolean))];
    return ["All", ...cats];
  }, [products]);

  const visible = filter === "All" ? products : products.filter((p) => p.category === filter);

  return (
    <section id="showcase" className="bg-sandsubtle py-20 sm:py-28 border-y border-[#E7DFD5]">
      <div className="section-shell">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-xl">
            <motion.p variants={REVEAL} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="eyebrow">
              03 / Signature Pieces
            </motion.p>
            <motion.h2
              variants={REVEAL} custom={1} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
              className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-walnut"
            >
              Handcrafted Showpieces, <span className="italic text-teak">Honest Pricing</span>
            </motion.h2>
            <motion.p
              variants={REVEAL} custom={2} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
              className="mt-4 text-base text-stone-600 leading-relaxed"
            >
              Every piece is built to order from seasoned timber. Enquire on WhatsApp for
              exact dimensions, finishes and delivery to your home.
            </motion.p>
          </div>
        </div>

        <div data-testid="product-filter-tabs" className="mt-10 flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <button
              key={tab}
              data-testid={`filter-tab-${tab.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setFilter(tab)}
              className={`rounded-full border px-4 py-2 text-xs font-semibold tracking-wide transition-all duration-200 ${
                filter === tab
                  ? "border-walnut bg-walnut text-sand"
                  : "border-[#E7DFD5] bg-white text-stone-600 hover:border-walnut hover:text-walnut"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        <motion.div layout data-testid="product-showcase-grid" className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.article
                layout
                key={p.id}
                data-testid={`product-item-${p.id}`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.45, ease: [0.215, 0.61, 0.355, 1] }}
                onClick={() => openProduct(p)}
                className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-[#E7DFD5] bg-white transition-shadow duration-500 hover:shadow-[0_24px_60px_-24px_rgba(46,28,20,0.35)]"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-sandsubtle">
                  <img
                    src={productImageSrc(p.image)}
                    onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                    alt={`${p.name} — ${p.category} by JK Furniture Mumbai`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  {p.tag && (
                    <span className="absolute left-4 top-4 rounded-full bg-brass px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-noir">
                      {p.tag}
                    </span>
                  )}
                  {typeof window !== "undefined" && localStorage.getItem("jk_admin_token") && (
                    <a
                      href="/admin"
                      data-testid={`product-edit-${p.id}`}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Edit ${p.name} in admin panel`}
                      title="You're the owner — tap to change this product's photos & details"
                      className="absolute right-4 top-4 flex items-center gap-1.5 rounded-full bg-noir/75 px-3 py-2 text-[11px] font-semibold text-sand opacity-0 backdrop-blur transition-opacity duration-200 hover:bg-noir group-hover:opacity-100"
                    >
                      <Pencil size={12} /> Edit
                    </a>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-teak">{p.category}</p>
                  <h3 className="mt-2 font-serif text-lg sm:text-xl text-walnut">{p.name}</h3>
                  {p.description && (
                    <p className="mt-2 text-sm leading-relaxed text-stone-600 line-clamp-3">{p.description}</p>
                  )}
                  {p.details && (
                    <p className="mt-3 text-xs font-mono tracking-wide text-stone-500">{p.details}</p>
                  )}
                  <div className="mt-4 flex items-baseline gap-2">
                    <span data-testid={`product-price-${p.id}`} className="font-serif text-2xl text-walnut">{p.price}</span>
                    {p.mrp && (
                      <span data-testid={`product-mrp-${p.id}`} className="text-sm text-stone-400 line-through">{p.mrp}</span>
                    )}
                  </div>
                  <a
                    data-testid={`btn-whatsapp-enquire-${p.id}`}
                    onClick={(e) => e.stopPropagation()}
                    href={waLink(productWaMessage(p))}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 inline-flex items-center justify-center gap-2 rounded-full border border-[#1EBE5D] px-5 py-3 text-sm font-semibold text-[#128C4B] transition-colors duration-200 hover:bg-[#25D366] hover:text-white hover:border-[#25D366]"
                  >
                    <MessageCircle size={16} />
                    Enquire on WhatsApp
                  </a>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        {visible.length === 0 && (
          <p className="mt-12 text-center text-sm text-stone-500" data-testid="showcase-empty-note">
            Pieces in this collection are being photographed — message us on WhatsApp for the latest catalogue.
          </p>
        )}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="product-modal"
            className="fixed inset-0 z-[70] flex items-center justify-center bg-noir/60 p-4 backdrop-blur-sm"
            onClick={() => setActive(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.35, ease: [0.215, 0.61, 0.355, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative grid max-h-[90vh] w-full max-w-5xl grid-cols-1 overflow-y-auto rounded-3xl bg-sand lg:grid-cols-2"
            >
              <button
                data-testid="product-modal-close"
                onClick={() => setActive(null)}
                aria-label="Close product details"
                className="absolute right-4 top-4 z-10 rounded-full bg-white/90 p-2 text-walnut shadow transition-colors hover:bg-white"
              >
                <X size={18} />
              </button>

              <div className="bg-sandsubtle p-4 sm:p-6">
                <div className="img-frame aspect-square w-full rounded-2xl">
                  <img
                    data-testid="product-modal-image"
                    src={productImageSrc(galleryOf(active)[imgIdx] || active.image)}
                    onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                    alt={`${active.name} — photo ${imgIdx + 1}`}
                    className="h-full w-full object-cover"
                  />
                </div>
                {galleryOf(active).length > 1 && (
                  <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                    {galleryOf(active).map((img, i) => (
                      <button
                        key={`${img}-${i}`}
                        data-testid={`product-modal-thumb-${i}`}
                        onClick={() => setImgIdx(i)}
                        aria-label={`View photo ${i + 1}`}
                        className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 transition-colors ${
                          i === imgIdx ? "border-brass" : "border-transparent opacity-70 hover:opacity-100"
                        }`}
                      >
                        <img
                          src={productImageSrc(img)}
                          onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex flex-col p-6 sm:p-8">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-teak">{active.category}</p>
                <h3 className="mt-2 font-serif text-2xl sm:text-3xl tracking-tight text-walnut">{active.name}</h3>
                <div className="mt-2 flex items-baseline gap-3">
                  <p className="font-serif text-2xl text-walnut">{active.price}</p>
                  {active.mrp && (
                    <p className="text-base text-stone-400 line-through">{active.mrp}</p>
                  )}
                </div>
                {active.description && (
                  <p className="mt-4 text-sm leading-relaxed text-stone-600">{active.description}</p>
                )}
                <dl className="mt-6 space-y-3 border-t border-[#E7DFD5] pt-5">
                  {active.wood && (
                    <div className="flex gap-3 text-sm">
                      <dt className="w-24 shrink-0 font-semibold text-walnut">Wood</dt>
                      <dd className="text-stone-600">{active.wood}</dd>
                    </div>
                  )}
                  {active.dimensions && (
                    <div className="flex gap-3 text-sm">
                      <dt className="w-24 shrink-0 font-semibold text-walnut">Dimensions</dt>
                      <dd className="text-stone-600">{active.dimensions}</dd>
                    </div>
                  )}
                  {active.finish && (
                    <div className="flex gap-3 text-sm">
                      <dt className="w-24 shrink-0 font-semibold text-walnut">Finish</dt>
                      <dd className="text-stone-600">{active.finish}</dd>
                    </div>
                  )}
                  {active.details && (
                    <div className="flex gap-3 text-sm">
                      <dt className="w-24 shrink-0 font-semibold text-walnut">Specs</dt>
                      <dd className="text-stone-600">{active.details}</dd>
                    </div>
                  )}
                </dl>
                <a
                  data-testid="product-modal-whatsapp-enquire"
                  href={waLink(productWaMessage(active))}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-semibold text-noir transition-colors hover:bg-[#1EBE5D]"
                >
                  <MessageCircle size={16} />
                  Enquire on WhatsApp
                </a>
                <p className="mt-3 text-xs leading-relaxed text-stone-500">
                  Need a different size or finish? Mention it in your message — every piece can be customized.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
